export type ProductCollectionFlagFilter = {
    isFeatured?: boolean;
    isClearance?: boolean;
    isAusMade?: boolean;
    isInStock?: boolean;
};

export type ProductCollectionFilter = {
    /** Existing singular filter keys kept for backwards compatibility. */
    parentId?: string[];
    categoryId?: string[];
    productId?: string[];

    /** Optional plural aliases for clearer usage in JSON/config. */
    parentIds?: string[];
    categoryIds?: string[];
    productIds?: string[];

    sortOrderFrom?: number;
    sortOrderTo?: number;

    /** Product flags, e.g. { isFeatured: true, isInStock: true }. */
    flags?: ProductCollectionFlagFilter;

    /** Matches product.basic.active. */
    active?: boolean;

    /** Matches product.basic.tags. Defaults to matching any supplied tag. */
    tags?: string[];

    /** Use "all" when every supplied tag must exist on the product. Defaults to "any". */
    tagMatch?: "any" | "all";
};

export type ProductFilterableItem = {
    parentId?: string;
    categoryId?: string;
    productId?: string;
    sortOrder?: number;
    flags?: ProductCollectionFlagFilter;
    basic?: {
        tags?: string[];
        active?: boolean;
        [key: string]: unknown;
    };
};

const normalizeIds = (value?: string[]) =>
    Array.isArray(value)
        ? value.map((item) => String(item).trim()).filter(Boolean)
        : [];

const normalizeTags = (value?: string[]) =>
    Array.isArray(value)
        ? value.map((item) => String(item).trim().toLowerCase()).filter(Boolean)
        : [];

const hasBooleanFlagFilters = (flags?: ProductCollectionFlagFilter) =>
    !!flags &&
    Object.values(flags).some((value) => typeof value === "boolean");

export const applyProductCollectionFilter = <T extends ProductFilterableItem>(
    items: T[],
    filter?: ProductCollectionFilter,
): T[] => {
    if (!Array.isArray(items) || !items.length || !filter) {
        return Array.isArray(items) ? items : [];
    }

    const parentIds = [
        ...normalizeIds(filter.parentId),
        ...normalizeIds(filter.parentIds),
    ];
    const categoryIds = [
        ...normalizeIds(filter.categoryId),
        ...normalizeIds(filter.categoryIds),
    ];
    const productIds = [
        ...normalizeIds(filter.productId),
        ...normalizeIds(filter.productIds),
    ];
    const tags = normalizeTags(filter.tags);
    const tagMatch = filter.tagMatch === "all" ? "all" : "any";
    const hasSortOrderFrom = typeof filter.sortOrderFrom === "number";
    const hasSortOrderTo = typeof filter.sortOrderTo === "number";
    const hasActiveFilter = typeof filter.active === "boolean";
    const hasFlagFilters = hasBooleanFlagFilters(filter.flags);

    return items.filter((item) => {
        if (parentIds.length && (!item.parentId || parentIds.indexOf(item.parentId) === -1)) {
            return false;
        }

        if (categoryIds.length && (!item.categoryId || categoryIds.indexOf(item.categoryId) === -1)) {
            return false;
        }

        if (productIds.length && (!item.productId || productIds.indexOf(item.productId) === -1)) {
            return false;
        }

        const sortOrder =
            typeof item.sortOrder === "number" ? item.sortOrder : Number.POSITIVE_INFINITY;

        if (hasSortOrderFrom && sortOrder < (filter.sortOrderFrom as number)) {
            return false;
        }

        if (hasSortOrderTo && sortOrder > (filter.sortOrderTo as number)) {
            return false;
        }

        if (hasFlagFilters && filter.flags) {
            const flagEntries = Object.entries(filter.flags) as Array<[
                keyof ProductCollectionFlagFilter,
                boolean | undefined,
            ]>;

            for (const [flagKey, expectedValue] of flagEntries) {
                if (typeof expectedValue !== "boolean") continue;

                if (item.flags?.[flagKey] !== expectedValue) {
                    return false;
                }
            }
        }

        if (hasActiveFilter && item.basic?.active !== filter.active) {
            return false;
        }

        if (tags.length) {
            const itemTags = normalizeTags(item.basic?.tags);

            if (!itemTags.length) {
                return false;
            }

            const matchesTags =
                tagMatch === "all"
                    ? tags.every((tag) => itemTags.indexOf(tag) !== -1)
                    : tags.some((tag) => itemTags.indexOf(tag) !== -1);

            if (!matchesTags) {
                return false;
            }
        }

        return true;
    });
};
